import { useSectionTitle } from 'components/section-menu/SectionActionsContext';
import {
    createContext,
    ReactNode,
    RefObject,
    useCallback,
    useContext,
    useId,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';

/** The Required section's title, and the key it goes by among the form's own sections, which go by their titles. */
export const REQUIRED_SECTION = 'create-dialog.required.title';

/** On the element that holds the dialog's sections. Only what the form declares under it is ever moved. */
const SECTIONS_ATTRIBUTE = 'data-create-dialog-sections';

/** On the place the form declares a field or a block at, which is empty while that is drawn in Required. */
export const HOME_ATTRIBUTE = 'data-create-dialog-home';

/** On what moved into Required a moment ago, or was mounted there by an act of the user, which the list tints. */
const ARRIVED_ATTRIBUTE = 'data-create-dialog-arrived';
const ARRIVED_MS = 2000;

/** On the host of a field, or of a block that counts as one, while it says it is left to fill. */
export const EMPTY_ATTRIBUTE = 'data-create-dialog-empty';

/**
 * One field of the form, as its own section and the Required section count it. A block that says whether it is
 * filled is one too, in place of the fields inside it.
 */
export type RequiredField = {
    name: string;
    /** The title of the section the form declares it in. */
    section: string;
    /** Drawn in the Required section rather than in its own. */
    inRequired: boolean;
    /** Required as the form stands, or held in Required by the block around it. */
    required: boolean;
    isEmpty: boolean;
    hasErrors: boolean;
};

type Placement = {
    /** The Required section's list, which the host of a required field is moved into. */
    list: HTMLElement;
    /** Records a field under the id of the component that draws it; `null` forgets it. */
    register: (id: string, field: RequiredField | null) => void;
};

/** What a block that moves as one tells the fields inside it, which are drawn wherever it is. */
type Block = {
    inRequired: boolean;
    /** Which of the fields inside count as required: those whose own `required` says so, all of them, or none. */
    counts: 'own' | 'all' | 'none';
    /** Whether the fields inside carry the required asterisk while the block is in Required. */
    starred: boolean;
};

const PlacementContext = createContext<Placement | null>(null);
const FieldsContext = createContext<ReadonlyMap<string, RequiredField>>(new Map());
const BlockContext = createContext<Block | null>(null);

function isSameField(a: RequiredField, b: RequiredField): boolean {
    return (
        a.name === b.name &&
        a.section === b.section &&
        a.inRequired === b.inRequired &&
        a.required === b.required &&
        a.isEmpty === b.isEmpty &&
        a.hasErrors === b.hasErrors
    );
}

/**
 * Knows every field the form declares in the dialog's sections and where each is drawn. `list` is the element
 * the Required section shows: it exists before any field renders, so a required field is in it from its first
 * paint.
 */
export function CreateDialogRequiredProvider({ list, children }: { list: HTMLElement; children: ReactNode }) {
    const [fields, setFields] = useState<ReadonlyMap<string, RequiredField>>(() => new Map());

    const register = useCallback<Placement['register']>((id, field) => {
        setFields((current) => {
            const known = current.get(id);
            if (field ? known !== undefined && isSameField(known, field) : known === undefined) {
                return current;
            }
            const next = new Map(current);
            if (field) {
                next.set(id, field);
            } else {
                next.delete(id);
            }
            return next;
        });
    }, []);

    const placement = useMemo(() => ({ list, register }), [list, register]);

    return (
        <PlacementContext.Provider value={placement}>
            <FieldsContext.Provider value={fields}>{children}</FieldsContext.Provider>
        </PlacementContext.Provider>
    );
}

/**
 * Every field the form declares in the dialog's sections, and every block that counts as one. None outside the
 * create dialog.
 */
export function useRequiredFields(): RequiredField[] {
    return Array.from(useContext(FieldsContext).values());
}

/** True where the dialog's sections draw a field, which may move it into Required. False on a page and in a popup. */
export function useInCreateDialogSections(): boolean {
    return useContext(PlacementContext) !== null;
}

/** True for a field inside a block that stars its fields, while that block is drawn in Required. */
export function useStarredInRequired(): boolean {
    const block = useContext(BlockContext);
    return block !== null && block.starred && block.inRequired;
}

function createHost(): HTMLElement {
    const host = document.createElement('div');
    // No box of its own: what it carries is laid out by the parent the host is in.
    host.className = 'contents';
    return host;
}

/** The home of each host in the Required list, which is what orders the list. */
const HOMES = new WeakMap<Element, Element>();
const ARRIVED_TIMERS = new WeakMap<Element, ReturnType<typeof setTimeout>>();
/** The Required lists of the forms the user has acted on. */
const ACTED_ON = new WeakSet<Element>();

/**
 * Says the user has acted on the form `list` belongs to. What the form mounts in Required from then on arrives
 * there, as a field that turns required does; what it mounted before that is what it opened with.
 */
export function noteUserActedOn(list: HTMLElement) {
    ACTED_ON.add(list);
}

type Parent = HTMLElement & { moveBefore?: (node: Node, child: Node | null) => void };

function move(host: HTMLElement, parent: Parent, before: Element | null) {
    if (host.parentNode === parent && host.nextElementSibling === before) {
        return;
    }
    const focused = host.contains(document.activeElement) ? document.activeElement : null;

    if (parent.moveBefore && host.isConnected && parent.isConnected) {
        // Keeps the focus where it is. Taking the host out of the document first would blur its field, and a
        // blur marks the field touched.
        parent.moveBefore(host, before);
    } else {
        parent.insertBefore(host, before);
        if (focused instanceof HTMLElement) {
            focused.focus({ preventScroll: true });
        }
    }
}

/**
 * Puts a host where its content is drawn: in the Required list, at its home's place in the form's order, or
 * back in its home. Says which, and `null` for a home outside the dialog's sections (a popover a section
 * renders), whose content never moves.
 */
function place(host: HTMLElement, home: HTMLElement, list: HTMLElement, inRequired: boolean): boolean | null {
    if (!home.closest(`[${SECTIONS_ATTRIBUTE}]`)) {
        move(host, home, null);
        return null;
    }
    if (!inRequired) {
        host.removeAttribute(ARRIVED_ATTRIBUTE);
        move(host, home, null);
        return false;
    }
    const arrives = host.parentNode === home || (host.parentNode === null && ACTED_ON.has(list));
    if (arrives) {
        host.setAttribute(ARRIVED_ATTRIBUTE, '');
        clearTimeout(ARRIVED_TIMERS.get(host));
        ARRIVED_TIMERS.set(
            host,
            setTimeout(() => host.removeAttribute(ARRIVED_ATTRIBUTE), ARRIVED_MS)
        );
    }
    HOMES.set(host, home);
    // ponytail: ordered against the hosts already listed, when this one is placed. A form that reorders
    // fields it keeps mounted is not re-sorted; place every host again on a reorder if one ever does.
    const next = Array.from(list.children).find((other) => {
        const otherHome = other === host ? undefined : HOMES.get(other);
        return (
            otherHome !== undefined &&
            (home.compareDocumentPosition(otherHome) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0
        );
    });
    move(host, list, next ?? null);
    return true;
}

/**
 * A home and a host. React renders the content into the host, once, and the host is what moves between its home
 * and the Required list: the content stays mounted, so a field keeps its value, its error, its touched state and
 * its DOM nodes.
 */
function useHost() {
    const homeRef = useRef<HTMLDivElement>(null);
    const [host] = useState(createHost);

    useLayoutEffect(() => () => host.remove(), [host]);

    return { homeRef, host };
}

type HostedProps = { homeRef: RefObject<HTMLDivElement | null>; host: HTMLElement; children: ReactNode };

/** React puts nothing in the home, so the host appended to it is safe from React. */
function Hosted({ homeRef, host, children }: HostedProps) {
    return (
        <>
            <div ref={homeRef} className="contents" data-create-dialog-home="" />
            {createPortal(children, host)}
        </>
    );
}

type FieldProps = {
    name: string;
    required?: boolean;
    isEmpty?: boolean;
    hasErrors: boolean;
    children: ReactNode;
};

/**
 * What `InputWrapper` draws its field through. In the create dialog's sections the field is drawn in the Required
 * section exactly while `required` is true, and where the form declares it otherwise. Anywhere else, a page or a
 * popup, this renders the field as it is.
 */
export function CreateDialogField({ children, ...field }: FieldProps) {
    const placement = useContext(PlacementContext);

    return placement ? (
        <PlacedField placement={placement} {...field}>
            {children}
        </PlacedField>
    ) : (
        children
    );
}

function PlacedField({
    placement: { list, register },
    name,
    required = false,
    isEmpty,
    hasErrors,
    children,
}: FieldProps & { placement: Placement }) {
    const block = useContext(BlockContext);
    const section = useSectionTitle();
    const id = useId();
    const { homeRef, host } = useHost();
    // Inside a block the field is drawn in place, wherever the block is, and does not move by itself.
    const blockInRequired = block ? block.inRequired : null;
    const counts = block?.counts ?? 'own';

    useLayoutEffect(() => {
        const home = homeRef.current;
        const inRequired = blockInRequired ?? (home ? place(host, home, list, required) : null);
        host.toggleAttribute(EMPTY_ATTRIBUTE, isEmpty === true);

        register(
            id,
            inRequired === null
                ? null
                : {
                      name,
                      section,
                      inRequired,
                      required: counts === 'own' ? required : counts === 'all',
                      isEmpty: isEmpty === true,
                      hasErrors,
                  }
        );
    }, [homeRef, host, list, register, id, name, section, required, isEmpty, hasErrors, blockInRequired, counts]);

    useLayoutEffect(() => () => register(id, null), [register, id]);

    return block ? (
        children
    ) : (
        <Hosted homeRef={homeRef} host={host}>
            {children}
        </Hosted>
    );
}

type BlockProps = Block & { isEmpty?: boolean; hasErrors?: boolean; children: ReactNode };
type MovingBlockProps = BlockProps & { placement: Placement };

function MovingBlock({
    placement: { list, register },
    inRequired,
    counts,
    starred,
    isEmpty,
    hasErrors = false,
    children,
}: MovingBlockProps) {
    const section = useSectionTitle();
    const id = useId();
    const { homeRef, host } = useHost();
    const block = useMemo(() => ({ inRequired, counts, starred }), [inRequired, counts, starred]);

    useLayoutEffect(() => {
        const home = homeRef.current;
        const placed = home ? place(host, home, list, inRequired) : null;
        host.toggleAttribute(EMPTY_ATTRIBUTE, isEmpty === true);

        // A block that says whether it is filled is one field of the form. Any other is none.
        register(
            id,
            placed === null || isEmpty === undefined
                ? null
                : { name: id, section, inRequired, required: inRequired, isEmpty, hasErrors }
        );
    }, [homeRef, host, list, register, id, section, inRequired, isEmpty, hasErrors]);

    useLayoutEffect(() => () => register(id, null), [register, id]);

    return (
        <Hosted homeRef={homeRef} host={host}>
            <BlockContext.Provider value={block}>{children}</BlockContext.Provider>
        </Hosted>
    );
}

/** Moves a block as one. A block inside another moves with the outer one, and so does not move by itself. */
function BlockSlot({ children, ...block }: BlockProps) {
    const placement = useContext(PlacementContext);
    const nested = useContext(BlockContext) !== null;

    return placement && !nested ? (
        <MovingBlock placement={placement} {...block}>
            {children}
        </MovingBlock>
    ) : (
        children
    );
}

type RequiredBlockProps = {
    when: boolean;
    /**
     * Whether the block is left to fill. A block that says so is one field of the form: it counts once, and the
     * fields inside it do not count as required, each for itself.
     */
    isEmpty?: boolean;
    /** With `isEmpty`: the block is one field to fix. */
    hasErrors?: boolean;
    /**
     * Stars the fields inside while the block is in Required: for a field the form only asks for as it submits,
     * whose own `required` is false until then. The asterisk is drawn, and nothing validates by it.
     */
    starred?: boolean;
    children: ReactNode;
};

/**
 * Draws `children` in the Required section of the create dialog while `when` holds, and where the form declares
 * them otherwise: a block that is not one input, or a field whose requirement the form only registers at submit.
 * The block moves as one and decides for the fields inside it, which all count as required while it is in
 * Required, unless the block counts as one itself (`isEmpty`). On a page and in a popup it renders `children` as
 * they are.
 */
export function CreateDialogRequiredBlock({ when, isEmpty, hasErrors, starred = false, children }: RequiredBlockProps) {
    return (
        <BlockSlot
            inRequired={when}
            counts={isEmpty === undefined ? 'all' : 'none'}
            starred={starred}
            isEmpty={isEmpty}
            hasErrors={hasErrors}
        >
            {children}
        </BlockSlot>
    );
}

/**
 * Draws `children` wherever the field named `field` is drawn: in the Required section of the create dialog while
 * that field is there, where the form declares them otherwise. For what belongs beside a field: the checkbox
 * that waives it, a banner or a panel under it. On a page and in a popup it renders `children` as they are.
 *
 * `field` is the input's `name`, its path in the form.
 */
export function CreateDialogFieldCompanion({ field, children }: { field: string; children: ReactNode }) {
    const inRequired = useRequiredFields().some(
        // `TextInput` names its field `field_` + its path.
        (known) => known.inRequired && (known.name === field || known.name === `field_${field}`)
    );

    return (
        <BlockSlot inRequired={inRequired} counts="own" starred={false}>
            {children}
        </BlockSlot>
    );
}
